/**
 * The project Home (design "A calmer project Home"): three questions in order, the rest folded away.
 *
 * 1. Is it working? (the strip)  2. What changed? (one list, WHAT_CHANGED_MAX items at most)  3. Where are things?
 * (Apps, Files), then "More about this project", closed, remembered open or closed per person in this browser.
 * A brand-new project (nothing to run, no files) shows "Start by…" in their place.
 */
import { participantOf } from './collab3Model';
import { liveRows } from './liveModel';
import { sinceAuthorName } from './presenceModel';

export const WHAT_CHANGED_MAX = 5;

const time = (iso) => Date.parse(iso || '') || 0;

/** Is an activity event yours (a person event; what your agents did is still news)? `you`: presence's `{ id, sub }`. */
function byYou(event, you) {
  if (!you) { return false; }
  const who = participantOf(event);
  if (!who || who.kind !== 'person') { return false; }
  const id = String(who.author?.id || '');
  return Boolean(id) && (id === you.id || (Boolean(you.sub) && id === `user:${you.sub}`));
}

/**
 * The rows of "What changed", in this order, at most `max`:
 * - the newest unread handoff note left by someone else, pinned while unread (`handoff`);
 * - who is live in the project now, one row (`live`: this project's entry of the `/api/v1/live` answer);
 * - Shadow AI, only when the project used an AI tool outside the approved list (`shadow`: the provenance's);
 * - then the latest activity since you left (`events`, newest first; `since` null: a first visit, everything shown),
 *   latest first (by when each ended), your own edits and the pinned note's own event left out.
 * `you`: presence's `{ id, sub }`. Each row: `{ kind: 'handoff' | 'live' | 'shadow' | 'event', key, ... }`.
 */
export function whatChangedItems({ handoff = null, moreNotes = 0, live = null, shadow = null, events = [], since = null, you = null, max = WHAT_CHANGED_MAX } = {}) {
  const items = [];
  if (handoff) { items.push({ kind: 'handoff', key: `handoff:${handoff.id}`, note: handoff, more: moreNotes }); }
  const rows = live ? liveRows(live, you) : [];
  if (rows.length > 0) {
    const person = (live.people || [])[0] || null;
    items.push({ kind: 'live', key: 'live', rows, follow: person });
  }
  if (shadow?.flagged) { items.push({ kind: 'shadow', key: 'shadow', shadow }); }
  const after = time(since);
  // An agent's run that went on after you left is news too (its `until`); the pinned note is not twice.
  const pinned = handoff?.id || null;
  (events || [])
    .filter((event) => (!after || Math.max(time(event.at), time(event.until)) > after) && !byYou(event, you))
    .filter((event) => !(pinned && event.type === 'handoff' && event.noteId === pinned))
    .sort((a, b) => Math.max(time(b.at), time(b.until)) - Math.max(time(a.at), time(a.until)))
    .forEach((event) => { items.push({ kind: 'event', key: `event:${event.id}`, event }); });
  return items.slice(0, max);
}

/**
 * "Luca 11 · Flow chat for Luca 9 · Sara 3": who made the changes since you left, from the since-last-seen answer.
 * `you`: presence's `{ id, sub }`.
 */
export function sinceAuthors(data, you = null, { max = 3 } = {}) {
  const me = you?.sub ? { id: you.sub } : null;
  const authors = (data?.authors || []).slice(0, max).map((a) => `${sinceAuthorName(a, me)} ${a.count}`);
  const more = (data?.authors || []).length - authors.length;
  return `${authors.join(' · ')}${more > 0 ? ` · ${more} more` : ''}`;
}

/** "23 changes in 9 files", or ''. */
export function changesInFiles(data) {
  const changes = data?.changes || 0;
  if (!changes) { return ''; }
  const files = (data.files || []).length;
  return `${changes} change${changes === 1 ? '' : 's'} in ${files} file${files === 1 ? '' : 's'}`;
}

/** "Luca is editing StoreMap.jsx; Flow chat for Luca is writing useNearby.js" (the live row's sentence). */
export function liveSentence(rows) {
  return (rows || []).map((row) => row.text).join('; ');
}

/**
 * A brand-new project: nothing to run and no files in or out yet, once all of it is known. Its Home is "Start by…"
 * (describe it in chat, or import a folder or a zip) instead of the empty sections.
 */
export function isBrandNew({ loaded, services = 0, inputs = 0, outputs = 0 }) {
  return Boolean(loaded) && services === 0 && inputs === 0 && outputs === 0;
}

const moreKey = (personId) => `flow.home.more.${personId || 'anonymous'}`;

/** Whether "More about this project" was left open by this person in this browser (closed by default). */
export function moreRemembered(personId) {
  try { return localStorage.getItem(moreKey(personId)) === 'open'; } catch { return false; }
}

export function rememberMore(personId, open) {
  try { localStorage.setItem(moreKey(personId), open ? 'open' : 'closed'); } catch { /* not remembered */ }
}

/**
 * "How it fits together · Brain · Conversations · Restore points · AI used": the parts of More that have something to
 * show (Restore points: the project has datastores and its sandbox can keep them).
 */
export function moreParts({ map = false, ai = false, conversations = false, restore = false }) {
  return [
    map && 'How it fits together',
    conversations && 'Conversations',
    restore && 'Restore points',
    ai && 'AI used',
  ].filter(Boolean);
}
